import React, { useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import moment from 'moment';

import { IClientCash, ICondoUnit } from '@share/common-types';
import { getSelectedCurrency } from '@share/utils';
import { renderNumber, getNights } from '@share/utils';
import { Currency, ModalCancellationPolicies } from '@components';
import { RootState } from '@share/utils';
import { IUnitsSearchState, IWeekDetailsState, weeksReviewBookAction } from '@store/slices';
import { IWeekState, weeksActions } from '@share/store/slices';
import { ILoginState } from '@share/store/slices';
import { Routes } from '@share/constants';
import { WalletPriceType, getWalletPrices } from '@utils';
import { RefundableEnum } from '@common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCalendarDays, faLock, faMoon, faUsers } from '@fortawesome/free-solid-svg-icons';

import { ModalUnitDetailsNew } from '../../../../../condo/condo-details/condo-units/modal-unit-details-new';

import './style.scss';

interface IMapStateToProps {
  weeksStore: IWeekState;
  weeksDetailsStore: IWeekDetailsState;
  unitsSearchStore: IUnitsSearchState;
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setSelectedWeeksSearchClientCash: (selectedClientCahs: IClientCash) => void;
  setSelectedWeeksReviewClientCash: (selectedClientCash: number) => void;
  setSelectedWeeksReviewClientCashStr: (clientCash: string) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  unit: ICondoUnit;
  condoId: number;
  isWalletSearch: boolean;
  isHotel: boolean;
}

const FACTOR = Math.pow(10, 0);

function WeeksUnitRowNewComponent(props: IProps) {
  const { unit, condoId, loginStore, weeksDetailsStore, weeksStore, history } = props;
  const { selectedWeeksSearchClientCash } = weeksStore;
  const {
    refundability,
    dateRange,
    price,
    publicPrice,
    unitId,
    name,
    cancellationPolicyText,
    bedroomsCount,
    bathroomsCount,
    maxOccupancy,
    maxWalletClientCash,
    sleepingArrangements,
  } = unit;
  const { account, userWalletData } = loginStore;

  const [isModalCancellationPoliciesVisible, setIsModalCancellationPoliciesVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const showCancellationPolicies = (): void => {
    setIsModalCancellationPoliciesVisible(true);
  };

  const handleCancelModalCancellationPolicies = (): void => {
    setIsModalCancellationPoliciesVisible(false);
  };

  const toggleDetailsModal = (): void => {
    setIsModalVisible(!isModalVisible);
  };

  const handleReviewCondo = async (accountName: string | undefined, condoIdParam: number, unitIdParam: string, walletPrices: any) => {
    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

    const selectedPropertyClientCash = displayWalletSavings ? walletPrices?.maxClientCashPermited : selectedWeeksSearchClientCash?.selectedPropertyClientCash;

    await props.setSelectedWeeksReviewClientCash(selectedPropertyClientCash);
    await props.setSelectedWeeksReviewClientCashStr(selectedPropertyClientCash ? selectedPropertyClientCash.toString() : '');
    await props.setSelectedWeeksSearchClientCash({ ...selectedWeeksSearchClientCash, selectedPropertyReviewClientCash: selectedPropertyClientCash });

    history.push(`/${accountName}${Routes.WeeksBook}/${condoIdParam}/${unitIdParam}${location.search}`);
  };

  const week = weeksDetailsStore?.week;

  const savings = (!!price && !!publicPrice && publicPrice - price >= 0) ? publicPrice - price : null;
  const savingsPercent = !!savings && !!publicPrice ? Math.round(((savings / publicPrice) * 100) * FACTOR) / FACTOR : 0;

  const priceInt = price;
  const maxWalletClientCashInt = maxWalletClientCash;
  const nightsCount = getNights(dateRange?.from, dateRange?.to) || 1;

  const currency = useMemo(() => getSelectedCurrency(account), [account]);
  const walletPrices = useMemo(
    () => getWalletPrices(account, userWalletData, selectedWeeksSearchClientCash, priceInt, maxWalletClientCashInt, WalletPriceType.Details),
    [account, userWalletData, selectedWeeksSearchClientCash, priceInt, maxWalletClientCashInt],
  );

  const finalPriceCalculated = priceInt - (walletPrices?.clientCashAmountApplied ? walletPrices?.clientCashAmountApplied : 0);
  const finalPriceMax = priceInt - (walletPrices?.maxClientCashAmountAllow ? walletPrices?.maxClientCashAmountAllow : 0);

  const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

  const finalPrice = displayWalletSavings ? finalPriceMax : finalPriceCalculated;
  const isRefundable = refundability === RefundableEnum.Refundable;

  const beds = (sleepingArrangements || []).filter(Boolean);
  const bedsText = beds.length ? beds.join(' · ') : '';

  const checkInText = dateRange?.from ? moment(dateRange.from).format('MMM D') : '';
  const checkOutText = dateRange?.to ? moment(dateRange.to).format('MMM D') : '';

  const onReserve = () => handleReviewCondo(account?.name, condoId, unitId, walletPrices);

  return (
    <div className="unit-card">
      <div className="unit-card__header">
        <h4 className="unit-card__name">
          <span className="unit-card__name-text" title={name}>
            {name?.replace('<[^>]+>', '')}
          </span>
          <span
            className="unit-card__more-info"
            onClick={toggleDetailsModal}
          >
            <FormattedMessage id="more.info" />
          </span>
        </h4>
        <button
          type="button"
          className="unit-card__more-link"
          onClick={toggleDetailsModal}
        >
          {bedroomsCount > 0 ? `${bedroomsCount}BR` : <FormattedMessage id="more.info" />}
        </button>
      </div>

      <div className="unit-card__badges">
        <span className="unit-card__beds-badge">
          {[
            bedroomsCount > 0 ? (
              <FormattedMessage
                key="br"
                id="bedrooms.count"
                values={{ count: bedroomsCount }}
              />
            ) : null,
            bathroomsCount > 0 ? (
              <FormattedMessage
                key="ba"
                id="bathrooms.count"
                values={{ count: bathroomsCount }}
              />
            ) : null,
            bedsText ? <span key="beds">{bedsText}</span> : null,
          ]
            .filter(Boolean)
            .reduce<React.ReactNode[]>((acc, node, i) => {
              if (i > 0) acc.push(<span key={`sep-${i}`}> · </span>);
              acc.push(node);
              return acc;
            }, [])}
        </span>
        <span
          className={`unit-card__refundable ${isRefundable ? 'ref' : 'non-ref'}`}
          onClick={showCancellationPolicies}
        >
          <FontAwesomeIcon icon={isRefundable ? faLock : faBan} />
          <FormattedMessage id={isRefundable ? 'refundable' : 'non.refundable'} />
        </span>
      </div>

      <div className="unit-card__info-line">
        <div className="unit-card__info-item">
          <FontAwesomeIcon icon={faCalendarDays} />
          <span>{checkInText} → {checkOutText}</span>
        </div>
        <div className="unit-card__info-item">
          <FontAwesomeIcon icon={faMoon} />
          <FormattedMessage id="nights" values={{ count: nightsCount }} />
        </div>
        <div className="unit-card__info-item">
          <FontAwesomeIcon icon={faUsers} />
          <FormattedMessage id="guests.count" values={{ count: maxOccupancy }} />
        </div>
      </div>

      <div className="unit-card__divider" />

      <div className="unit-card__footer">
        <div className="unit-card__price-block">
          <div className="unit-card__price-line">
            <div className="unit-card__price-total">
              <Currency currency={currency} />
              {renderNumber(Math.floor(finalPrice))}
            </div>
            <div className="unit-card__price-taxes">
              <FormattedMessage id="abbreviated.price.includes.taxes" />
            </div>
          </div>
          {(!!savingsPercent && savingsPercent > 0 && !displayWalletSavings) ? (
            <div className="unit-card__price-savings">
              <FormattedMessage id={account?.forceMemberWording ? 'member.save' : 'save'} /> {savingsPercent}%
            </div>
          ) : null}
          {(displayWalletSavings && walletPrices?.priceSavings > 0) ? (
            <div className="unit-card__price-savings">
              <FormattedMessage id="save.up.to" values={{ number: walletPrices?.priceSavings ? walletPrices?.priceSavings : '<1' }} />
            </div>
          ) : null}
        </div>

        <button type="button" className="unit-card__reserve" onClick={onReserve}>
          <FormattedMessage id="reserve" />
        </button>
      </div>

      <ModalUnitDetailsNew
        condo={week?.propertyDetails}
        unit={unit}
        visible={isModalVisible}
        onCancel={toggleDetailsModal}
        walletPrices={walletPrices}
        adultsCount={null}
        childrenCount={null}
        savings={savings}
        savingsPercent={savingsPercent}
        onReserve={() => {
          toggleDetailsModal();
          onReserve();
        }}
      />

      <ModalCancellationPolicies
        isForCondo={true}
        cancellationPoliciesText={cancellationPolicyText}
        visible={isModalCancellationPoliciesVisible}
        onCancel={handleCancelModalCancellationPolicies}
      />
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    weeksStore: state.weeksStore,
    weeksDetailsStore: state.weeksDetailsStore,
    unitsSearchStore: state.unitsSearchStore,
    loginStore: state.loginStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setSelectedWeeksSearchClientCash: weeksActions.setSelectedWeeksSearchClientCash,
  setSelectedWeeksReviewClientCash: weeksReviewBookAction.setSelectedWeeksReviewClientCash,
  setSelectedWeeksReviewClientCashStr: weeksReviewBookAction.setSelectedWeeksReviewClientCashStr,
};

export const WeeksUnitRowNew = connect(mapStateToProps, mapDispatchToProps)(withRouter(WeeksUnitRowNewComponent));
